import { ReactNode } from 'react';

import Loading from '@/components/utils/Loading';
import NoAccess from '@/pages/NoAccess';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
// TODO: feature_flag_cleanup: hasAccountPermissions - Remove this import when FF is fully rolled out
import { checkAccess } from '@/providers/UserPermissionsProvider/hooks/rbacFallback';
import usePermissionAccess from '@/providers/UserPermissionsProvider/hooks/usePermissionAccess';
// TODO: feature_flag_cleanup: hasAccountPermissions - Remove this import when FF is fully rolled out
import useRoleAccess from '@/providers/UserPermissionsProvider/hooks/useRoleAccess';
import { Permission } from '@/utils/permissionTypes';
import { UserRole } from '@/utils/roleUtils';

interface WithPermissionAccessProps {
  children?: ReactNode;
  /**
   * Permission to check (e.g., PERMISSIONS.CAMPAIGNS_READ)
   */
  permission: Permission;
  /**
   * TODO: feature_flag_cleanup: hasAccountPermissions - Remove this prop when FF is fully rolled out
   * Role to check when hasAccountPermissions flag is OFF (RBAC fallback)
   */
  role?: UserRole;
  /**
   * Optional custom fallback component when access is denied
   */
  fallbackComponent?: ReactNode;
}

/**
 * Route guard component that checks permissions before rendering children.
 * Controlled by `hasAccountPermissions` feature flag.
 *
 * When flag is ON: Uses PBAC (permission check)
 * When flag is OFF: Uses RBAC (role from PERMISSION_TO_ROLE_MAP)
 *
 * @example
 * <WithPermissionAccess permission="campaigns-edit">
 *   <CampaignEditPage />
 * </WithPermissionAccess>
 */
const WithPermissionAccess = ({
  children,
  permission,
  role,
  fallbackComponent,
}: WithPermissionAccessProps) => {
  const { currentClient } = useSession();
  const hasAccountPermissions = currentClient?.hasAccountPermissions ?? false;

  const { hasPermission, isInitialLoad: isPermissionLoading } =
    usePermissionAccess();
  // TODO: feature_flag_cleanup: hasAccountPermissions - Remove useRoleAccess when FF is fully rolled out
  const { hasRole, isInitialLoad: isRoleLoading } = useRoleAccess();

  if (isPermissionLoading || isRoleLoading) {
    return <Loading height="100vh" width="100vw" />;
  }

  // TODO: feature_flag_cleanup: hasAccountPermissions - Replace with just hasPermission(permission)
  const hasAccess = checkAccess({
    permission,
    role,
    hasAccountPermissions,
    hasPermission,
    hasRole,
  });

  if (!hasAccess) {
    if (fallbackComponent) {
      return fallbackComponent;
    }
    return <NoAccess />;
  }

  return children;
};

export default WithPermissionAccess;
